/**
 * Shared Google Maps JavaScript API bootstrap（MAP-GOOGLE-RENDERER-C1）。
 * Places 與 Maps renderer 共用單一 script insert 與 singleton promise。
 */

const SCRIPT_ATTR = 'data-formosa-google-maps-bootstrap';

const IMPORT_LIBRARY_POLL_MS = 50;
const IMPORT_LIBRARY_TIMEOUT_MS = 15000;

let bootstrapPromise = null;
/** @type {Map<string, Promise<unknown>>} */
const libraryPromises = new Map();

function isImportLibraryReady() {
  return typeof window.google?.maps?.importLibrary === 'function';
}

/**
 * script onload 後 importLibrary 可能尚未掛上；輪詢直到就緒或逾時。
 * @returns {Promise<void>}
 */
function waitForImportLibrary(timeoutMs = IMPORT_LIBRARY_TIMEOUT_MS) {
  if (isImportLibraryReady()) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      if (isImportLibraryReady()) {
        resolve();
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        reject(new Error('Google Maps importLibrary unavailable after script load'));
        return;
      }
      window.setTimeout(tick, IMPORT_LIBRARY_POLL_MS);
    };
    tick();
  });
}

function rejectIfInvalidBrowser(apiKey) {
  if (!apiKey || !String(apiKey).trim()) {
    return Promise.reject(new Error('Google Maps browser key is missing'));
  }
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Google Maps is only available in the browser'));
  }
  return null;
}

/**
 * 共用 bootstrap：全 app 只插入一個 Maps JS script。
 * @param {string} apiKey browser key
 * @returns {Promise<void>}
 */
export function ensureGoogleMapsBootstrap(apiKey) {
  const invalid = rejectIfInvalidBrowser(apiKey);
  if (invalid) return invalid;
  const trimmedKey = String(apiKey).trim();
  if (isImportLibraryReady()) {
    return Promise.resolve();
  }
  if (bootstrapPromise) {
    return bootstrapPromise;
  }

  bootstrapPromise = new Promise((resolve, reject) => {
    const finishBootstrap = () => {
      waitForImportLibrary()
        .then(resolve)
        .catch((err) => {
          bootstrapPromise = null;
          reject(err);
        });
    };

    const existing = document.querySelector(`script[${SCRIPT_ATTR}]`);
    if (existing) {
      if (existing.complete) {
        finishBootstrap();
        return;
      }
      existing.addEventListener('load', finishBootstrap, { once: true });
      existing.addEventListener('error', () => {
        bootstrapPromise = null;
        reject(new Error('Google Maps script failed to load'));
      }, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.setAttribute(SCRIPT_ATTR, '1');
    script.async = true;
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(trimmedKey)}`
      + '&v=weekly&loading=async';
    script.onload = finishBootstrap;
    script.onerror = () => {
      bootstrapPromise = null;
      reject(new Error('Google Maps script failed to load'));
    };
    document.head.appendChild(script);
  });

  return bootstrapPromise;
}

/**
 * Dynamic library import；bootstrap 後快取各 library promise。
 * @param {string} apiKey browser key
 * @param {'places' | 'maps' | string} libraryName
 * @returns {Promise<unknown>}
 */
export function importGoogleMapsLibrary(apiKey, libraryName) {
  const invalid = rejectIfInvalidBrowser(apiKey);
  if (invalid) return invalid;
  const key = String(apiKey).trim();
  const lib = String(libraryName || '').trim();
  if (!lib) {
    return Promise.reject(new Error('Google Maps library name is missing'));
  }
  const cacheKey = lib;
  if (!libraryPromises.has(cacheKey)) {
    libraryPromises.set(cacheKey, ensureGoogleMapsBootstrap(key).then(() =>
      window.google.maps.importLibrary(lib),
    ));
  }
  return libraryPromises.get(cacheKey);
}

/** 測試用：重置 loader 狀態。 */
export function __resetGoogleMapsLoaderForTests() {
  bootstrapPromise = null;
  libraryPromises.clear();
}

export const __testExports = {
  waitForImportLibrary,
  isImportLibraryReady,
  IMPORT_LIBRARY_POLL_MS,
  SCRIPT_ATTR,
};
